import { performInMemorySearch } from '@/components/search/searchStore';
import Vue from 'vue';
import api from './api';
import MODULE_ID from './moduleId';
import dataUtils from '@/common/dataUtils';
import worksheetService from '@/modules/customerVbzWorksheet/worksheetService';

const DEBUG = false;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const MS_ONE_HOUR = 60 * 1000;
function setUpdate(state, item) {
  if (!item) return;
  item._updated = true;
  state.absencesTableUpdated = true;
  setTimeout(() => {
    console.debug('delete ._updated flag');
    state.absencesTableUpdated = false;
    item._updated = false;
  }, 15000);
}
function setToStartOfDay(date) {
  const d = new Date(date.getTime());
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  console.debug(`setToStartOfDay=${d.toISOString()}`);
  return d;
}

function prepareDayFilterArgs(state) {
  let date = state.contextDate;
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  let datePlusOneDay = new Date(date.getTime() + ONE_DAY_MS);
  const dateMs = date.getTime();
  const datePlusOneDayMs = datePlusOneDay.getTime();
  return { date, datePlusOneDay, dateMs, datePlusOneDayMs };
}

const state = {
  contextDate: setToStartOfDay(new Date()), // Date object
  displayMode: 'grid',
  view: 'dispo', // content to display absence table, admin to display administrative functions
  hasJoinedSocketRoom: false,
  pikettDisplayMode: 'active',
  infosDisplayMode: 'active',
  showReadAbsenceItems: false,
  showReadInfoItems: true,

  users: [],
  userByCompanyNr: {},
  userByEmployeeNr: {},
  userBossesByUserId: {},

  absences: [],
  piketts: [],
  informations: [],

  absencesByDate: {},
  pikettsByDate: {},
  informationsByDate: {},

  stations: [],
  clients: [],

  // lookup tables
  remarks: [],
  reasons: [],
  illnessReasons: [],
  betriebshofs: [],
  absenceDurations: [],
  zueri9Reasons: [],
  zueri9Clients: [],
  dispoStates: [],
  zueri9States: [],
  zueri9MonthlyExpectedHours: [],

  // lookup tables by id
  remarksById: {},
  reasonsById: {},
  illnessReasonsById: {},
  betriebshofsById: {},
  absenceDurationsById: {},
  zueri9ReasonsById: {},
  zueri9ClientsById: {},
  dispoStatesById: {},
  zueri9StatesById: {},
  zueri9MonthlyExpectedHoursById: {},

  // Ui states
  absenceEditDialog: false, //show / hide absence edit dialog
  expandedFirstRow: false,
  absencesTableUpdated: false,

  dutyStopsById: {},

  //all data loaded
  dataLoaded: false,
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  /**
   * Returns all pikett items with a start date on the current contextDate
   * @param {*} state
   * @returns
   */
  getPikettsPerDay: (state) => {
    let { date, datePlusOneDay, dateMs, datePlusOneDayMs } = prepareDayFilterArgs(state);

    let piketts = state.piketts.filter((pikett) => {
      if (state.pikettDisplayMode === 'active' && pikett.deleted === true) return false;
      if (state.pikettDisplayMode === 'deleted' && pikett.deleted === false) return false;

      // Adding 2 hours avoids that 00.00 h entries are "moved" to the previous day
      let startMs = Date.parse(pikett.start) + MS_ONE_HOUR * 2;
      return startMs >= dateMs && startMs < datePlusOneDayMs;
    });
    return piketts;
  },

  getUnreadCountByModuleInstance: (state) => (instance) => {
    return 0;
  },
  /**
   * Calculate Züri 9 assignements per day and month.
   * Filter: Calculate only those entries marked as F=Fahrdienst (Für wen) and one of T=Tram, B=Bus, TB=Trolleybus in (Für wen)
   * Calculation rule: Calculate sum of dutyFrom-dutyUntil of filtered items for each day of the current month. Extrapolate the average to the end of the month. Target per day is 100hours
   * @param state
   */
  getFahrdienstStats: (state) => {
    let sumOfDay = {};
    state.absences.forEach((absence) => {
      let zueri9WorkingFor = state.zueri9ClientsById[absence.zuer9forId];
      let dutyFrom = Date.parse(absence.dutyFrom);
      let dutyUntil = Date.parse(absence.dutyUntil);
    });
  },
};

export const mutations = {
  setModuleData(state, { users, userByCompanyNr, userByEmployeeNr, userBossesByUserId, dutyStopsById }) {
    state.users = users;
    state.userByCompanyNr = userByCompanyNr;
    state.userByEmployeeNr = userByEmployeeNr;
    state.userBossesByUserId = userBossesByUserId;
    state.dutyStopsById = dutyStopsById;

    state.dataLoaded = true;
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    console.debug('countModuleItems');
    // not implemented
  },
  setView: (state, viewType) => (state.view = viewType),
  setAbsenceEditDialog: (state, { value }) => {
    state.absenceEditDialog = value;
  },
  setHasJoinedSocketRoom: (state, hasJoinedSocketRoom = false) => {
    console.debug('setHasJoinedSocketRoom', hasJoinedSocketRoom);
    state.hasJoinedSocketRoom = hasJoinedSocketRoom;
  },
  setDisplayMode: (state, value) => {
    state.displayMode = value;
  },
  set: (state, { property, value }) => {
    Vue.set(state, property, value);
  },

  /**
   * Use to store all items of the given domain.
   * Param domain can be reason,remark, betriebshof
   */
  saveDomainItems: (state, { domain, items }) => {
    // console.debug(`saveDomainItems ${domain}`)
    if (
      [
        'reason',
        'remark',
        'illnessReason',
        'betriebshof',
        'pikett',
        'absence',
        'absenceDuration',
        'information',
        'zueri9Client',
        'zueri9Reason',
        'zueri9State',
        'dispoState',
        'zueri9MonthlyExpectedHour',
      ].indexOf(domain) >= 0
    ) {
      // apply localStorage information (Info Tab) read (or better delete) information which is stored locally only
      if (domain === 'information') {
        let markedAsRead = JSON.parse(localStorage.getItem('vbzWorksheet/infos/markedAsRead') || '[]');
        let itemsById = {};
        items.forEach((item) => (itemsById[item.id] = item));
        markedAsRead.forEach((id) => {
          if (itemsById[id]) {
            itemsById[id].deleted = true;
          }
        });
      }
      if (['absence', 'pikett', 'information'].includes(domain)) {
        console.debug(`Patch ${domain}s, that is replace existing items and push new items`);
        let existingItems = state[`${domain}s`];
        if (existingItems.length === 0) {
          items.forEach((i) => {
            i.lock = null;
          });
          state[`${domain}s`] = items;
        } else {
          let newItems = [];
          let existingItemsById = dataUtils.buildIdMap(existingItems, 'id');
          items.forEach((item) => {
            // console.debug(`Item updated: ${item._updated} or created:${item._created} ${JSON.stringify(item, null, 2)}`);

            if (item._updated || item._created) {
              newItems.push(item);
            }
            existingItemsById[item.id] = item;
          });
          console.debug('received newItems', JSON.stringify(newItems, null, 2));
          if (newItems.length > 0) {
            worksheetService.clearUpdateAndCreatedMarks(newItems, 15);
          }

          state[`${domain}s`] = Object.keys(existingItemsById).map((id) => existingItemsById[id]);
        }
      } else {
        state[`${domain}s`] = items;
      }

      postSetDomainItems(state, state[`${domain}s`], domain);
    }
  },
  updateItem: (state, { oldItem, newItem }) => {
    Object.keys(newItem).forEach((k) => {
      oldItem[k] = newItem[k];
    });

    setUpdate(state, oldItem);
  },
  pushDomainItem: (state, { domain, item }) => {
    console.debug('pushDomainItem', domain, item);
    state[`${domain}s`].push(item);
    postSetDomainItems(state, state[`${domain}s`], domain);
    setUpdate(state, item);
  },

  setContextDate: (state, value) => {
    state.contextDate = setToStartOfDay(value);
  },
  /** Mutation called by store/search orchestrator */
  setFilterCriteria(state, { filterCriteria }) {
    state.filterCriteria = filterCriteria ? filterCriteria.toLowerCase().split(' ') : [];
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
  /**
   * This getter is used by overall search and performs a module specific search. For simplicity it uses the performInMemorySearch which does a lot for the module (including server requests).
   * Search all documents containing all of the space separated search words in state.filterCriteria
   *
   * @param {*} state
   * @param {*} getters
   * @param {*} rootState
   * @param {*} rootGetters
   */
  searchModuleInMemoryContainingSearchTerm(state, getters, rootState, rootGetters) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.worksheet,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: rootState.search.filterCriteria,
    });
  },

  async fetchAllData({ commit }) {
    //['reason', 'remark', 'illnessReason', 'betriebshof', 'pikett', 'absence', 'absenceDuration', 'information'
    let calls = [
      api.fetchAll('reason'),
      api.fetchAll('illnessReason'),
      api.fetchAll('betriebshof'),
      api.fetchAll('remark'),
      api.fetchAll('pikett'),
      api.fetchAll('absence'),
      api.fetchAll('information'),
      api.fetchAll('absenceDuration'),
      api.fetchAll('zueri9Client'),
      api.fetchAll('zueri9Reason'),
      api.fetchAll('dispoState'),
      api.fetchAll('zueri9State'),
      api.fetchAll('zueri9MonthlyExpectedHour'),

      api.loadUserData(),
      api.loadModuleData(),
    ];
    Promise.all(calls)
      .then(
        ([
          reasons,
          illnessReasons,
          betriebshofs,
          remarks,
          piketts,
          absences,
          informations,
          absenceDurations,
          zueri9Client,
          zueri9Reason,
          dispoState,
          zueri9State,
          zueri9MonthlyExpectedHour,
          userData,
          moduleData,
        ]) => {
          commit('saveDomainItems', { domain: 'reason', items: reasons.data });
          commit('saveDomainItems', { domain: 'illnessReason', items: illnessReasons.data });
          commit('saveDomainItems', { domain: 'betriebshof', items: betriebshofs.data });
          commit('saveDomainItems', { domain: 'remark', items: remarks.data });
          commit('saveDomainItems', { domain: 'pikett', items: piketts.data });
          commit('saveDomainItems', { domain: 'absence', items: absences.data });
          commit('saveDomainItems', { domain: 'zueri9Client', items: zueri9Client.data });
          commit('saveDomainItems', { domain: 'zueri9Reason', items: zueri9Reason.data });

          commit('saveDomainItems', { domain: 'absenceDuration', items: absenceDurations.data });
          commit('saveDomainItems', { domain: 'dispoState', items: dispoState.data });
          commit('saveDomainItems', { domain: 'zueri9State', items: zueri9State.data });
          commit('saveDomainItems', { domain: 'zueri9MonthlyExpectedHour', items: zueri9MonthlyExpectedHour.data });

          // Enhance infos by locally stored deleted marks (called markedAsRead because infos (chat messages) were thought to be marked as read as absences)
          let infos = informations.data;
          let infoIdsMarkedAsReadStr = localStorage.getItem('vbzWorksheet/infos/markedAsRead');
          if (infoIdsMarkedAsReadStr) {
            let infoIdsMarkedAsRead = JSON.parse(infoIdsMarkedAsReadStr);
            infoIdsMarkedAsRead.forEach((id) => {
              let item = infos.find((i) => i.id === id);
              if (item) item.deleted = true;
            });
          }
          commit('saveDomainItems', { domain: 'information', items: infos });

          let { users, userBosses } = userData.data;
          let userByCompanyNr = dataUtils.buildIdMap(users, 'companynr');
          let userByEmployeeNr = dataUtils.buildIdMap(users, 'employeenr');

          let userBossesByUserId = dataUtils.buildIdMap(userBosses, 'userId');
          let dutyStopsById = dataUtils.buildIdMap(moduleData.data.dutyStops, 'id');
          commit('setModuleData', { users, userByCompanyNr, userByEmployeeNr, userBossesByUserId, dutyStopsById });
        }
      )
      .catch((err) => {
        console.error('Failed to load WorksheetData');
        throw err;
      });
  },

  /**
   * !!! Module-implementation of mandatory action, dispatched in store/sync !!!
   * ------------------------------------------------------------------------------------------------------------------
   * Get this modules server meta data * See modules/documents/store action synchronizeData for a detailed description
   * ------------------------------------------------------------------------------------------------------------------
   * No-Op implementation here as module will work in online mode only.
   * Do not remove this action though!!!
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    DEBUG && console.debug('Action: documents/synchronizeData ');
    console.debug('sync worksheet');
    await dispatch('fetchAllData');
  },
  /**
   * !!! Module-implementation of mandatory action, dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * Each module needs to implement this function.
   */
  async synchronizeFiles({ commit, rootState, state, rootGetters }) {
    // Not implemented here
  },
};
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

function postSetDomainItems(state, items, domain) {
  // Create array maps by date for absences, pikett and information
  const dateAttributesByDomain = { absence: 'dutyFrom', pikett: 'start', information: 'date' };
  let dateAttr = dateAttributesByDomain[domain];
  if (dateAttr) {
    console.time(`buildItemsByDate ${domain}`);
    let itemsByDate = worksheetService.buildItemsByDate(items, dateAttr);
    console.timeEnd(`buildItemsByDate ${domain}`);

    state[`${domain}sByDate`] = itemsByDate;
  } else {
    // create id maps for all other domains
    state[`${domain}sById`] = dataUtils.buildIdMap(items, 'id');
    // console.debug(`create id map for ${domain}`, state[`${domain}sById`]);
  }
}
